import React, { useEffect, useState } from 'react';
import { FormControlLabel, Stack, Switch, Typography } from '@mui/material';
import { SwapHorizOutlined } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getAuthHeaders, makeApiCall } from '../utils/apiConfig';
import { useAppTranslation } from '../i18n/useAppTranslation';
import { useOrgFeatureFlag } from '../hooks/useOrgFeatureFlags';
import { useActingStoreAdmin } from '../hooks/useActingStoreAdmin';
import { useToast } from '../contexts/ToastContext';
import { useUnsavedChangesSource } from './appUpdate/unsavedChanges';
import { invalidateTradeInCredits } from '../hooks/useTradeInCredits';
import { InlineMessage, LoadingState, SaveBar, SectionCard } from './ui';

export interface TradeInCreditDisplaySetting {
  enabled: boolean;
  updatedAt: string | null;
}

export const TRADE_IN_CREDITS_QUERY_KEY = ['store-trade-in-credit-display'] as const;
const ENDPOINT = '/store/settings/trade-in-credits';

/**
 * Jordan chooses whether the store shows a product's live trade-in credit
 * beside its price (flag `trade_in_credit_display`, MOT-742). Display only:
 * prices never change. Owner/admin edit; everyone else reads.
 */
export const TradeInCreditsCard: React.FC = () => {
  const { t } = useAppTranslation();
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const { enabled: flagOn } = useOrgFeatureFlag('trade_in_credit_display');
  const { canManage } = useActingStoreAdmin();
  const [shown, setShown] = useState(false);

  const query = useQuery({
    queryKey: TRADE_IN_CREDITS_QUERY_KEY,
    queryFn: async (): Promise<TradeInCreditDisplaySetting> => {
      const response = await makeApiCall(ENDPOINT, { headers: await getAuthHeaders() });
      const body = await response.json().catch(() => ({}));
      if (!response.ok || !body?.success) throw new Error(body?.error?.message || body?.message || t('store.tradeInCredits.loadError'));
      return body.data as TradeInCreditDisplaySetting;
    },
    enabled: flagOn,
  });
  const data = query.data;

  const discard = React.useCallback(() => setShown(data?.enabled ?? false), [data]);
  useEffect(() => {
    discard();
  }, [discard]);

  const dirty = canManage && !!data && data.enabled !== shown;

  const save = useMutation({
    mutationFn: async () => {
      const response = await makeApiCall(ENDPOINT, {
        method: 'PUT',
        headers: { ...(await getAuthHeaders()), 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled: shown }),
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok || !body?.success) throw new Error(body?.error?.message || body?.message || t('store.tradeInCredits.saveError'));
      return body.data as TradeInCreditDisplaySetting;
    },
    onSuccess: (saved) => {
      queryClient.setQueryData(TRADE_IN_CREDITS_QUERY_KEY, saved);
      queryClient.invalidateQueries({ queryKey: ['store-pricing-breakdown'] });
      // The product lines read through their own batched cache, not React Query.
      invalidateTradeInCredits();
      showToast(t('store.tradeInCredits.saved'), 'success');
    },
    onError: (error: Error) => showToast(error.message, 'error'),
  });
  useUnsavedChangesSource({ dirty, save: () => save.mutateAsync(), discard });

  if (!flagOn) return null;

  return (
    <SectionCard title={t('store.tradeInCredits.title')} leading={<SwapHorizOutlined color="primary" />}>
      {query.isLoading ? (
        <LoadingState />
      ) : query.isError || !data ? (
        <InlineMessage severity="error">{t('store.tradeInCredits.loadError')}</InlineMessage>
      ) : (
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            {t('store.tradeInCredits.intro')}
          </Typography>
          <FormControlLabel
            control={<Switch checked={shown} onChange={(event) => setShown(event.target.checked)} disabled={!canManage} />}
            label={t('store.tradeInCredits.switchLabel')}
          />
          <Typography variant="caption" color="text.secondary">
            {t(canManage ? 'store.tradeInCredits.priceNote' : 'store.tradeInCredits.readOnly')}
          </Typography>
          {canManage && (
            <SaveBar
              dirty={dirty}
              message={t('store.tradeInCredits.saveMessage')}
              saving={save.isPending}
              onSave={() => save.mutate()}
              onDiscard={discard}
            />
          )}
        </Stack>
      )}
    </SectionCard>
  );
};

export default TradeInCreditsCard;
